import cn from 'classnames'
import { forwardRef } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { toFormat } from 'core/classes/helpers/DateHelper'
import {
  ESP_PORTAL_URL,
  ESP_TRANSLATION_KEYS,
  EspBadge,
  type IEspActivity,
  type TEspSponsorshipStatus,
} from 'partner360/features/executiveSponsorProgram'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { CockpitIconButton } from '../../components/cockpitIconButton'
import { getSponsorInitials } from '../lib/getSponsorInitials'
import { ClockIcon, HandshakeIcon } from '../lib/icons'

import { CockpitEspQuarters } from './CockpitEspQuarters'

import styles from './CockpitEspSheet.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.esp

const formatDate = (value: number) => toFormat(value, 'DD MMM YYYY')

/** A request waiting for the program team: whom it names and when it was sent. */
export interface ICockpitEspRequestNote {
  sponsorName: string
  createdAt: number
}

export interface ICockpitEspSheetProps {
  status: TEspSponsorshipStatus
  sponsorName: string
  sponsorTitle: string
  nomination: ICockpitEspRequestNote | null
  change: ICockpitEspRequestNote | null
  activities: IEspActivity[]
  /** Days since the latest engagement, `null` when none was logged. */
  daysSinceLast: number | null
  isLate: boolean
  /** The current quarter's engagement is logged, so the next one waits for the next quarter. */
  loggedQuarter: string | null
  top: number
  onNominate: () => void
  onLog: () => void
  onOpenActivity: (activityId: string) => void
  onRequestChange: () => void
  onClose: () => void
}

/**
 * The panel the sponsor chip opens under the cockpit's top row, across the deck: the program's name
 * on a grey strip at the left edge, then the sponsor, the engagements by quarter and the actions.
 * Without an active sponsor the first column says where the partner stands and the others wait.
 */
export const CockpitEspSheet = forwardRef<HTMLDivElement, ICockpitEspSheetProps>(
  (
    {
      status,
      sponsorName,
      sponsorTitle,
      nomination,
      change,
      activities,
      daysSinceLast,
      isLate,
      loggedQuarter,
      top,
      onNominate,
      onLog,
      onOpenActivity,
      onRequestChange,
      onClose,
    },
    ref
  ) => {
    const t = useSafetyTranslation()
    const isActive = status === 'active'

    return (
      <div
        ref={ref}
        className={styles.root}
        style={{ top }}
        role="dialog"
        aria-label={t(KEYS.program)}
      >
        <div className={styles.strip}>
          <span className={styles.stripLabel}>
            <HandshakeIcon className={styles.stripIcon} />
            <Translation text={KEYS.program} span />
          </span>
        </div>

        <div className={cn(styles.column, styles.sponsorColumn)}>
          {isActive ? (
            <div className={styles.sponsorBody}>
              <div className={styles.card}>
                <span className={cn(styles.avatar, styles.avatarActive)}>
                  {getSponsorInitials(sponsorName)}
                </span>
                <div className={styles.cardText}>
                  <div className={styles.name}>{sponsorName}</div>
                  {sponsorTitle && <div className={styles.title}>{sponsorTitle}</div>}
                  <div className={styles.badge}>
                    <EspBadge variant="positive" labelKey={ESP_TRANSLATION_KEYS.status.active} />
                  </div>
                </div>
              </div>
              {change && (
                <p className={styles.secondLine}>
                  <Translation
                    text={KEYS.changeRequested}
                    replaceParams={[
                      { key: '{sponsor}', value: change.sponsorName },
                      { key: '{date}', value: formatDate(change.createdAt) },
                    ]}
                    span
                  />
                </p>
              )}
            </div>
          ) : status === 'pending' ? (
            <div className={styles.sponsorBody}>
              <div className={styles.badgeAbove}>
                <EspBadge variant="pending" labelKey={ESP_TRANSLATION_KEYS.status.pending} />
              </div>
              <p className={styles.note}>
                <ClockIcon className={styles.noteIcon} />
                <Translation
                  text={KEYS.nominationSent}
                  replaceParams={[
                    { key: '{sponsor}', value: nomination?.sponsorName ?? '' },
                    {
                      key: '{date}',
                      value: nomination ? formatDate(nomination.createdAt) : '',
                    },
                  ]}
                  span
                />
              </p>
            </div>
          ) : (
            <div className={styles.sponsorBody}>
              <div className={styles.badgeAbove}>
                <EspBadge variant="neutral" labelKey={ESP_TRANSLATION_KEYS.status.notOnboarded} />
              </div>
              <p className={styles.note}>
                <HandshakeIcon className={styles.noteIconLarge} />
                <Translation text={KEYS.about} span />
              </p>
              <Button color="primary" variant="contained" onClick={onNominate}>
                <Translation text={KEYS.nominate} />
              </Button>
            </div>
          )}

          <div className={styles.portal}>
            <span className={styles.portalText}>
              <Translation text={KEYS.portalText} span />
            </span>
            <CockpitIconButton
              showLabel
              icon="kbase kbase-external-link"
              label={t(KEYS.portal)}
              href={ESP_PORTAL_URL}
              target="_blank"
            />
          </div>
        </div>

        <div className={styles.column}>
          {isActive ? (
            <CockpitEspQuarters activities={activities} onOpenActivity={onOpenActivity} />
          ) : (
            <p className={styles.secondLine}>
              <Translation text={KEYS.engagementsLocked} span />
            </p>
          )}
        </div>

        <div className={cn(styles.column, styles.actions)}>
          {isActive && (
            <>
              <p className={styles.since}>
                {isLate && (
                  <span className={styles.late} aria-hidden="true">
                    !
                  </span>
                )}
                {daysSinceLast == null ? (
                  <Translation text={KEYS.noEngagement} span />
                ) : (
                  <Translation
                    text={KEYS.lastEngagement}
                    replaceParams={[{ key: '{days}', value: `<strong>${daysSinceLast}</strong>` }]}
                    span
                  />
                )}
              </p>
              <Button
                color="primary"
                variant="contained"
                disabled={Boolean(loggedQuarter)}
                onClick={onLog}
              >
                <Translation text={KEYS.log} />
              </Button>
              {loggedQuarter && (
                <p className={styles.secondLine}>
                  <Translation
                    text={KEYS.quarterLogged}
                    replaceParams={[{ key: '{quarter}', value: loggedQuarter }]}
                    span
                  />
                </p>
              )}
              {!change && (
                <Button color="primary" variant="text" onClick={onRequestChange}>
                  <Translation text={KEYS.requestChange} />
                </Button>
              )}
            </>
          )}
        </div>

        <span className={styles.close}>
          <CockpitIconButton icon="kbase kbase-close" label={t(KEYS.close)} onClick={onClose} />
        </span>
      </div>
    )
  }
)

CockpitEspSheet.displayName = 'CockpitEspSheet'
